/**
 * MapDrawer — the map's tab in the global drawer (PRD §4c "The drawer").
 *
 * Content only: ImpactMapView registers it with useDrawer() as a tab next to
 * the info and chat tabs, and pads the canvas by the drawer's width while any
 * tab is open — the same arrangement SolutionsScreen uses — so the map gets
 * narrower rather than covered. Three sections follow §4c's rule:
 *
 *   Kartalla      — structure. Layers you can hide, and the Ehdota actions
 *                   that add to the map (find loops, find cross-links).
 *   Kysy kartalta — one reading at a time: none / choices / loops / blocker
 *                   reach. Two at once is the overlay that turned into noise.
 *   Analyysi      — the Analysoi button, the latest result, earlier runs.
 *
 * The AI runs on the whole map — typing the edges, the loop and cross-link
 * searches, Analysoi — are the facilitator's (ai-and-people, #1408): the room
 * sees their results and decides the proposals, but only a sign-in with
 * facilitate on this Kuutio (or an admin) gets the buttons, as on the server.
 */
import React from 'react';
import styled from '@emotion/styled';
import { GitBranchPlus, Ban, Waypoints, GitFork, RefreshCw, ShieldCheck, Milestone } from 'lucide-react';
import ImpactMapAnalysisPanel from './ImpactMapAnalysisPanel';
import { useTranslation } from '../../hooks/useTranslation.jsx';
import useInstanceGrant from '../../hooks/useInstanceGrant';

const Content = styled.div`min-width: 0; padding: 14px 14px 20px; display: flex; flex-direction: column; gap: 18px; color: #e5e7eb;`;
const Section = styled.section`display: flex; flex-direction: column; gap: 8px;`;
const H = styled.h3`margin: 0; font-size: 0.72rem; font-weight: 700; letter-spacing: 0.08em; text-transform: uppercase; color: #9ca3af;`;
const Hint = styled.p`margin: 0; font-size: 0.78rem; color: #6b7280; line-height: 1.4;`;
const Row = styled.button`
  display: flex; align-items: center; gap: 10px; width: 100%; text-align: left; padding: 8px 10px; border-radius: 8px;
  border: 1px solid ${(p) => (p.$on ? (p.$color || '#7c3aed') : 'rgba(255,255,255,0.08)')};
  background: ${(p) => (p.$on ? 'rgba(255,255,255,0.05)' : 'transparent')}; color: #e5e7eb; font: inherit; cursor: pointer;
  &:hover { background: rgba(255,255,255,0.07); } &:disabled { opacity: .5; cursor: default; }
`;
const Count = styled.span`margin-left: auto; font-size: 0.78rem; color: #9ca3af; font-variant-numeric: tabular-nums;`;
const Pill = styled.span`font-size: 0.72rem; padding: 1px 8px; border-radius: 999px; background: #7c3aed; color: #fff;`;
const Action = styled.button`
  padding: 7px 12px; border-radius: 999px; border: 1px solid rgba(255,255,255,0.15); background: transparent; color: #e5e7eb; font: inherit; font-size: 0.85rem; cursor: pointer;
  &:hover { background: rgba(255,255,255,0.07); } &:disabled { opacity: .5; cursor: progress; }
`;

function Switch({ on, icon: Icon, label, count, color, onToggle, title }) {
  return (
    <Row type="button" $on={on} $color={color} onClick={onToggle} aria-pressed={on} title={title}>
      <Icon size={16} color={on ? color || '#a78bfa' : '#6b7280'} />
      <span style={{ opacity: on ? 1 : 0.7 }}>{label}</span>
      {count !== undefined && <Count>{count}</Count>}
    </Row>
  );
}

export default function MapDrawer({
  instanceId, onFocusNodes,
  // structure layers
  showConsequences, onShowConsequencesChange,
  showBlockers, onShowBlockersChange, blockerCount = 0,
  showGlobalLinks, onShowGlobalLinksChange, crossLinkCount = 0,
  factCheck, onFactCheckChange,
  typedEdges = 0, totalEdges = 0, aiTypedEdges = 0, onTypeEdges, typingEdges = false,
  // readings
  showChoices, onShowChoicesChange, choiceCount = 0, valenceJudged = false,
  showLoops, onShowLoopsChange, loopCount = 0,
  hideBlockedDownstream, onHideBlockedDownstreamChange,
  focus, onFocusChange,
  // Ehdota
  onFindLoops, onFindCrossLinks, pendingLoops = [], pendingCross = [], onOpenPending, busy = null,
  pulse = null,
  // Edistyminen: towns, milestones and goals only (progressView.js)
  progressView = false, onProgressViewToggle,
}) {
  const { t } = useTranslation();
  const canRunAi = useInstanceGrant(instanceId, 'facilitate');
  // The current reading, derived from the same flags the map already uses.
  // A reading dims the map to one question; a layer stays drawn. Silmukat is
  // a layer (saved loops, and the handle that draws a link from a node), so
  // asking about loops turns it on but leaving the question leaves it be.
  const reading = showChoices ? 'choices' : focus === 'loops' ? 'loops' : (hideBlockedDownstream || focus === 'blockers') ? 'blockers' : 'none';
  const ask = (next) => {
    const v = next === reading ? 'none' : next;
    onShowChoicesChange?.(v === 'choices');
    if (v === 'loops' && !showLoops) onShowLoopsChange?.(true);
    onHideBlockedDownstreamChange?.(v === 'blockers');
    onFocusChange?.(v === 'loops' ? 'loops' : v === 'blockers' ? 'blockers' : null);
  };

  return (
        <Content>
          <Section>
            <H>Kartalla</H>
            <Switch on={progressView} icon={Milestone} label={t('impactPath.route.progress')} color="#a78bfa" onToggle={() => onProgressViewToggle?.()} title={t('impactPath.route.progressHint')} />
            <Switch on={showConsequences} icon={GitBranchPlus} label="Seuraukset" color="#5B9DD9" onToggle={() => onShowConsequencesChange?.(!showConsequences)} />
            <Switch on={showBlockers} icon={Ban} label="Esteet" count={blockerCount} color="#ef5350" onToggle={() => onShowBlockersChange?.(!showBlockers)} />
            <Switch on={showGlobalLinks} icon={Waypoints} label="Ristiinyhteydet" count={crossLinkCount} color="#42a5f5" onToggle={() => onShowGlobalLinksChange?.(!showGlobalLinks)} />
            <Switch on={showLoops} icon={RefreshCw} label="Silmukat" count={loopCount} color="#7c3aed" onToggle={() => onShowLoopsChange?.(!showLoops)} title="Piirtää silmukat ja kahvan, josta yhteyden voi vetää solmusta toiseen" />
            <Switch on={factCheck} icon={ShieldCheck} label="AI-faktantarkistus" color="#a78bfa" onToggle={() => onFactCheckChange?.(!factCheck)} title="Tarkistaa uudet solmut tietopohjaa vasten" />
            <Hint title="Kytkentätyypin voi valita seurausta lisätessä; muut näkyvät oletuksena 'johtaa'.">
              Kytkentätyypit: {typedEdges}/{totalEdges} ihmisen valitsemaa{aiTypedEdges > 0 ? `, ${aiTypedEdges} AI:n` : ''}
            </Hint>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 4 }}>
              {canRunAi && (
                <>
                  <Action type="button" disabled={typingEdges || typedEdges + aiTypedEdges >= totalEdges} onClick={onTypeEdges} title="AI lukee jokaisen tyypittämättömän kytkennän ketjussaan ja ehdottaa tyypin. Ihmisen valinta säilyy.">{typingEdges ? 'Tyypitetään…' : 'Tyypitä kytkennät'}</Action>
                  <Action type="button" disabled={busy === 'loops'} onClick={onFindLoops}>{busy === 'loops' ? 'Etsitään…' : 'Ehdota silmukoita'}</Action>
                  <Action type="button" disabled={busy === 'cross'} onClick={onFindCrossLinks}>{busy === 'cross' ? 'Etsitään…' : 'Ehdota ristiinyhteyksiä'}</Action>
                </>
              )}
              {pendingLoops.length > 0 && <Action type="button" onClick={() => onOpenPending?.('loops')}><Pill>{pendingLoops.length}</Pill> silmukkaa tarkistamatta</Action>}
              {pendingCross.length > 0 && <Action type="button" onClick={() => onOpenPending?.('cross')}><Pill>{pendingCross.length}</Pill> yhteyttä tarkistamatta</Action>}
            </div>
            <Hint>Ehdotukset menevät jonoon; karttaan vasta kun joku hyväksyy.</Hint>
            {pulse && (
              <Hint title={pulse.at ? `Pulssi ${new Date(pulse.at).toLocaleTimeString('fi-FI', { hour: '2-digit', minute: '2-digit' })}` : undefined}>
                💓 Vipupiste-ehdokkaita {pulse.candidates}{pulse.newSince ? `, ${pulse.newSince} uutta` : ''} · <a href={`/pivotpoints-v2?instance=${instanceId}`} style={{ color: '#c4b5fd' }}>Vipupisteet</a>
              </Hint>
            )}
          </Section>

          <Section>
            <H>Kysy kartalta</H>
            <Switch on={reading === 'choices'} icon={GitFork} label="Missä valinnat ovat?" count={valenceJudged ? choiceCount : '–'} color="#0099ff" onToggle={() => ask('choices')} />
            <Switch on={reading === 'loops'} icon={RefreshCw} label="Mihin systeemi taipuu?" count={loopCount} color="#7c3aed" onToggle={() => ask('loops')} />
            <Switch on={reading === 'blockers'} icon={Ban} label="Mikä tukkii?" count={blockerCount} color="#ef5350" onToggle={() => ask('blockers')} />
            <Hint>Yksi lukutapa kerrallaan. Kartta himmentää kaiken, mikä ei vastaa kysymykseen.</Hint>
          </Section>

          <Section style={{ flex: 1, minHeight: 0 }}>
            <H>Analyysi</H>
            <ImpactMapAnalysisPanel inline instanceId={instanceId} onFocusNodes={onFocusNodes} canRun={canRunAi} />
          </Section>
        </Content>
  );
}
